import React from "react";
import store from "../store";
import { Col, Row, Typography, Tooltip, Button } from "antd";
import { useTranslation } from 'react-i18next';
import { LogoutOutlined, TrophyOutlined, MessageOutlined, ShareAltOutlined, SoundOutlined, SoundFilled } from '@ant-design/icons';
import { getWebSocket } from "../Socket";
import BlueBackCard from '../cards/BLUE_BACK.svg';
import HandSelectionModal from './HandSelectionModal';
import { shareRoom, getCurrentRoomId } from '../utils/shareUtils';
import Avatar from './Avatar';
import soundService from '../utils/soundService';
import { isMobile } from '../utils/deviceUtils';

const { Text } = Typography;

interface GameBoardState {
  cards: string[];
  cardsInTheMiddle: string[];
  canMove: boolean;
  playerName: string;
  currentPlayer: string;
  currentHand: string;
  top: string;
  left: string;
  right: string;
  isMyTurn: boolean;
  showHandModal: boolean;
  availableHands: string[];
  canPickHand: boolean;
  previousCardCount: number;
  lastPlayedBy: string;
  soundEnabled: boolean;
  lastHandForDealSound: string | null;
}

interface GameBoardProps {
  t: any;
  onShowScoreBoard?: () => void;
  onShowChat?: () => void;
  onLogout?: () => void;
}

class GameBoardComponent extends React.Component<GameBoardProps, GameBoardState> {

  constructor(props: GameBoardProps) {
    super(props);
    this.state = {
      cards: [],
      cardsInTheMiddle: [],
      canMove: false,
      playerName: "",
      currentPlayer: "",
      currentHand: "",
      top: "",
      left: "",
      right: "",
      isMyTurn: false,
      showHandModal: false,
      availableHands: [],
      canPickHand: false,
      previousCardCount: 0,
      lastPlayedBy: "",
      soundEnabled: soundService.isAudioEnabled(),
      lastHandForDealSound: null
    }
  }


  componentDidMount(): void {
    store.subscribe(() => {
      let state = store.getState();
      const canPickHand = this.getCanPickHand(state);
      const prevCanPickHand = this.state.canPickHand;
      
      // Get new values
      const newCards = this.getCards(state);
      const newCardsInTheMiddle = this.getCardsInTheMiddle(state);
      const newCanMove = this.getCanMove(state);
      const newPlayerName = this.getPlayerName(state);
      const newCurrentPlayer = this.getCurrentPlayer(state);
      const newCurrentHand = this.getCurrentHand(state);
      const newAvailableHands = this.getAvailableHands(state);
      
      // Only update if data actually changed
      const shouldUpdate = (
        JSON.stringify(newCards) !== JSON.stringify(this.state.cards) ||
        JSON.stringify(newCardsInTheMiddle) !== JSON.stringify(this.state.cardsInTheMiddle) ||
        newCanMove !== this.state.canMove ||
        newPlayerName !== this.state.playerName ||
        newCurrentPlayer !== this.state.currentPlayer ||
        newCurrentHand !== this.state.currentHand ||
        JSON.stringify(newAvailableHands) !== JSON.stringify(this.state.availableHands) ||
        canPickHand !== this.state.canPickHand
      );
      
      if (shouldUpdate) {
        // Track if new cards were added to determine who played them
        const cardCountIncreased = newCardsInTheMiddle.length > this.state.previousCardCount;
        // The person who played the last card is the PREVIOUS current player (before turn change)
        const lastPlayedBy = cardCountIncreased ? this.state.currentPlayer : this.state.lastPlayedBy;
        
        // Deal cards sound removed
        
        // Play card sound when any player plays a card (cards in middle increased)
        if (cardCountIncreased) {
          soundService.playCardSound();
        }
        
        this.setState({
          cards: newCards,
          cardsInTheMiddle: newCardsInTheMiddle,
          canMove: newCanMove,
          playerName: newPlayerName,
          currentPlayer: newCurrentPlayer,
          currentHand: newCurrentHand,
          isMyTurn: newCanMove,
          availableHands: newAvailableHands,
          canPickHand: canPickHand,
          previousCardCount: newCardsInTheMiddle.length,
          lastPlayedBy: lastPlayedBy,
          lastHandForDealSound: this.state.lastHandForDealSound,
          // Auto-show modal when it becomes user's turn to pick a hand
          showHandModal: canPickHand && !prevCanPickHand ? true : this.state.showHandModal
        });
        this.getPlayers(state);
      }
    })
  }

  getPlayers(state: any): any {
    let players: string[] = state.kingGame.gameState.players;
    let id: number = state.kingGame.gameState.playerId;
    let rightId: number = (id + 1) % 4;
    let topId: number = (id + 2) % 4;
    let leftId: number = (id + 3) % 4;

    let myMap = new Map();
    if (players) {
      players.forEach(s => {
        myMap.set(s.split("#")[0], s.split("#")[1]);
      });
    }

    let ses = new Map();
    ses.set("left", myMap.get(leftId + ""));
    ses.set("top", myMap.get(topId + ""));
    ses.set("right", myMap.get(rightId + ""));

    this.setState({
      left: ses.get("left"),
      right: ses.get("right"),
      top: ses.get("top")
    })

    return ses;
  }

  getCards(state: any): string[] {
    return state.kingGame.gameState.cards;
  }

  getCardsInTheMiddle(state: any): string[] {
    return state.kingGame.gameState.cardsInTheMiddle;
  }

  getPlayerName(state: any): string {
    const gameState = state.kingGame.gameState;
    const players = gameState.players;
    const playerId = gameState.playerId;
    
    // Get the current player's name from the players array using their playerId
    if (players && playerId !== -1) {
      const playerEntry = players.find((p: string) => {
        const [id] = p.split("#");
        return parseInt(id) === playerId;
      });
      
      if (playerEntry) {
        const [, name] = playerEntry.split("#");
        return name || gameState.username;
      }
    }
    
    // Fallback to username if players array not available
    return gameState.username;
  }

  getCurrentPlayer(state: any): string {
    return state.kingGame.gameState.currentPlayerName;
  }

  getCanMove(state: any): boolean {
    const gameState = state.kingGame.gameState;
    return gameState.currentHand != null && gameState.playerId === gameState.currentPlayer;
  }

  getAvailableHands(state: any): string[] {
    return state.kingGame.gameState.availableHands || [];
  }

  getCurrentHand(state: any): string {
    return state.kingGame.gameState.currentHand || "";
  }

  getCanPickHand(state: any): boolean {
    const gameState = state.kingGame.gameState;
    return gameState.currentHand == null && gameState.playerId === gameState.currentPlayer;
  }

  moveCard(card: string): void {
    if (!this.state.canMove) {
      return;
    }
    
    let data = {
      "id": "move",
      "card": card
    };
    
    const ws = getWebSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
    }
  }

  showHandModal = () => {
    this.setState({ showHandModal: true });
  }

  toggleSound = () => {
    const newSoundState = soundService.toggle();
    this.setState({ soundEnabled: newSoundState });
  }

  hideHandModal = () => {
    this.setState({ showHandModal: false });
  }

  handleShareRoom = async () => {
    const roomId = getCurrentRoomId();
    if (roomId) {
      try {
        await shareRoom({ roomId });
      } catch (error) {
        console.error('Failed to share room:', error);
      }
    }
  }


  renderPlayerPosition(playerName: string, isCurrentPlayer: boolean, isMyPosition: boolean = false) {
    
    return (
      <div className={`player-position ${isMyPosition ? 'main-player' : 'other-player'}`} style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '12px',
        borderRadius: '16px',
        background: 'rgba(30, 41, 59, 0.6)',
        backdropFilter: 'blur(10px)',
        border: isCurrentPlayer ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(71, 85, 105, 0.3)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 100,
        isolation: 'isolate',
        boxShadow: isCurrentPlayer 
          ? '0 8px 25px rgba(102, 126, 234, 0.2), 0 0 0 1px rgba(102, 126, 234, 0.1)' 
          : '0 4px 12px rgba(0, 0, 0, 0.15)',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px'
        }}>
          <Avatar 
            name={playerName}
            size={42}
            isCurrentPlayer={isCurrentPlayer}
            showBorder={true}
            isMobileOtherPlayer={!isMyPosition}
          />
          <div style={{ textAlign: 'center' }}>
            <div className="player-name-text" style={{
              fontSize: '0.85rem',
              fontWeight: isCurrentPlayer ? 600 : 500,
              color: isCurrentPlayer ? '#a5b4fc' : '#e2e8f0',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
              textAlign: 'center',
              letterSpacing: '0.025em'
            }}>
              {playerName || 'Waiting...'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  getCardImage(card: string, isBack: boolean = false): string {
    if (isBack) {
      return BlueBackCard;
    }
    
    // Try to require the card image, fallback to blue back if not found
    try {
      return require('../cards/' + card + '.svg');
    } catch (error) {
      console.error('Could not load card:', card, error);
      return BlueBackCard; // fallback to blue back
    }
  }

  renderCard(card: string, isPlayable: boolean = false, isBack: boolean = false, key?: string, animationClass?: string) {
    const cardSrc = this.getCardImage(card, isBack);
    
    // Use provided animation class or default based on card state
    const defaultAnimationClass = isBack ? 'card-back' : 'card-deal';
    const finalAnimationClass = animationClass || defaultAnimationClass;
    
    return (
        <img 
          className={`card ${isPlayable ? 'card-playable' : ''} ${finalAnimationClass}`}
          src={cardSrc} 
          onClick={() => isPlayable && this.moveCard(card)}
          style={{
            cursor: isPlayable ? 'pointer' : 'default',
            filter: isBack ? 'brightness(0.8)' : 'none'
          }}
          alt={card}
          onError={(e) => {
            console.error('Failed to load card image:', card);
            // Fallback to blue back on error
            (e.target as HTMLImageElement).src = BlueBackCard;
          }}
        />
    );
  }

  // Helper to get animation direction based on current player
  getPlayerAnimationDirection(currentPlayer: string): string {
    const state = store.getState() as any;
    const players = state.kingGame.gameState.players;
    const playerId = state.kingGame.gameState.playerId;
    
    if (!players || players.length === 0) return 'bottom';
    
    // Find the current player's ID from players array
    let currentPlayerId = -1;
    for (const player of players) {
      const [id, name] = player.split('#');
      if (name === currentPlayer) {
        currentPlayerId = parseInt(id);
        break;
      }
    }
    
    if (currentPlayerId === -1 || currentPlayerId === playerId) return 'bottom';
    
    // Calculate relative position (0=bottom/self, 1=right, 2=top, 3=left)
    const relativePosition = (currentPlayerId - playerId + 4) % 4;
    
    switch (relativePosition) {
      case 1: return 'right';
      case 2: return 'top';
      case 3: return 'left';
      default: return 'bottom';
    }
  }

  // Memoized center play area to prevent unnecessary re-renders
  renderCenterPlayArea() {
    const { cardsInTheMiddle, lastPlayedBy } = this.state;
    
    return (
      <div className={`center-play-area ${cardsInTheMiddle.length > 0 ? 'has-cards' : ''}`}>
        {cardsInTheMiddle.length > 0 ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            {cardsInTheMiddle.map((card, index) => {
              // Use lastPlayedBy for the most recently played card (last in array)
              const isNewCard = index === cardsInTheMiddle.length - 1;
              const animationDirection = isNewCard && lastPlayedBy ? this.getPlayerAnimationDirection(lastPlayedBy) : '';
              const animationClass = isNewCard && animationDirection ? `card-play-from-${animationDirection}` : 'card-deal';
              
              return (
                <div key={`${card}-${index}`} style={{ animationDelay: isNewCard ? '0s' : `${index * 0.1}s` }}>
                  {this.renderCard(card, false, false, `${card}-${index}`, animationClass)}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="center-play-area-empty">
          </div>
        )}
      </div>
    );
  }

  renderHand(cards: string[], isActive: boolean = false, isBack: boolean = false) {
    return (
      <div className={`hand hand-compact ${isActive ? 'active-hand' : ''}`}>
        {cards.map((card, index) => 
          this.renderCard(card, isActive && !isBack, isBack, `${card}-${index}`)
        )}
      </div>
    );
  }

  render(): React.ReactNode {
    const { 
      cards, 
      cardsInTheMiddle, 
      canMove, 
      playerName, 
      currentPlayer,
      top, 
      left, 
      right,
      showHandModal,
      canPickHand
    } = this.state;


    // Create dummy cards for other players (same count as your cards)
    const otherPlayerCards = cards.map(() => 'BLUE_BACK');

    return (
      <div className="game-board">
        {/* Compact Top Bar */}
        <div className="game-top-bar" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 8px',
          background: 'var(--bg-secondary)',
          borderRadius: '6px',
          marginBottom: '8px',
          minHeight: '32px',
          border: '1px solid var(--border-color)'
        }}>
          {/* Current Hand Display */}
          <div style={{ textAlign: 'left', flex: 1 }}>
            {(() => {
              const state = store.getState();
              const currentHand = this.getCurrentHand(state);
              const availableHands = this.getAvailableHands(state);
              const canPickHand = this.getCanPickHand(state);
              
              if (currentHand) {
                return (
                  <div 
                    style={{
                      color: 'var(--primary-color)',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: availableHands.length > 0 ? 'pointer' : 'default',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={availableHands.length > 0 ? this.showHandModal : undefined}
                  >
                    {this.props.t(`hands.handNames.${currentHand}`, currentHand)}
                  </div>
                );
              } else if (availableHands.length > 0 && canPickHand) {
                return (
                  <div 
                    style={{
                      color: 'var(--accent-color)',
                      fontSize: '10px',
                      fontWeight: 600,
                      animation: 'pulse 2s infinite',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={this.showHandModal}
                  >
                    {this.props.t('hands.chooseYourHand')}
                  </div>
                );
              }
              // Hide room name in single player mode
              const gameState = store.getState() as any;
              const singlePlayer = gameState.kingGame?.gameState?.singlePlayer || false;
              
              if (singlePlayer) {
                return (
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '10px',
                    fontWeight: 600
                  }}>
                    Single Player
                  </div>
                );
              }
              
              return (
                <div style={{
                  color: 'var(--text-secondary)',
                  fontSize: '10px',
                  fontWeight: 600
                }}>
                  {(window as any).currentRoom || 'Game Room'}
                </div>
              );
            })()}
          </div>

          {/* Round Progress */}
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2px',
            margin: '0 8px'
          }}>
            {[1, 2, 3, 4].map((index) => (
              <div
                key={index}
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  backgroundColor: index <= cardsInTheMiddle.length 
                    ? 'var(--primary-color)' 
                    : 'var(--bg-primary)',
                  transition: 'all 0.3s ease',
                  transform: index <= cardsInTheMiddle.length ? 'scale(1.1)' : 'scale(1)'
                }}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '8px',
            alignItems: 'center'
          }}>
            <div className="mobile-only" style={{ display: 'flex', gap: '8px' }}>
              <Tooltip title="Scores">
                <Button
                  type="text"
                  size="small"
                  icon={<TrophyOutlined />}
                  onClick={this.props.onShowScoreBoard}
                  style={{
                    color: 'var(--text-secondary)',
                    border: 'none',
                    width: '44px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    borderRadius: '6px',
                    background: 'rgba(255,255,255,0.05)',
                    minWidth: '44px',
                    touchAction: 'manipulation'
                  }}
                />
              </Tooltip>
{!isMobile() && (
              <Tooltip title="Chat">
                <Button
                  type="text"
                  size="small"
                  icon={<MessageOutlined />}
                  onClick={this.props.onShowChat}
                  style={{
                    color: 'var(--text-secondary)',
                    border: 'none',
                    width: '44px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    borderRadius: '6px',
                    background: 'rgba(255,255,255,0.05)',
                    minWidth: '44px',
                    touchAction: 'manipulation'
                  }}
                />
              </Tooltip>
              )}
              {(() => {
                // Only show share button for multiplayer games
                const gameState = store.getState() as any;
                const gameMode = gameState.kingGame?.gameState?.gameMode;
                const singlePlayer = gameState.kingGame?.gameState?.singlePlayer;
                
                if (gameMode === 'multiplayer' || (!singlePlayer && gameMode !== 'single')) {
                  return (
                    <Tooltip title={this.props.t('share.shareRoom')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<ShareAltOutlined />}
                        onClick={this.handleShareRoom}
                        style={{
                          color: 'var(--accent-color)',
                          border: 'none',
                          width: '44px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          borderRadius: '6px',
                          background: 'rgba(99,102,241,0.1)',
                          minWidth: '44px',
                          touchAction: 'manipulation'
                        }}
                      />
                    </Tooltip>
                  );
                }
                return null;
              })()}
            </div>
            <Tooltip title={this.state.soundEnabled ? "Disable Sound" : "Enable Sound"}>
              <Button
                type="text"
                size="small"
                icon={this.state.soundEnabled ? <SoundFilled /> : <SoundOutlined />}
                onClick={this.toggleSound}
                style={{
                  color: this.state.soundEnabled ? 'var(--primary-color)' : 'var(--text-secondary)',
                  border: 'none',
                  width: '44px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  borderRadius: '6px',
                  background: this.state.soundEnabled ? 'rgba(123,104,238,0.1)' : 'rgba(255,255,255,0.05)',
                  minWidth: '44px',
                  touchAction: 'manipulation'
                }}
              />
            </Tooltip>
            <Tooltip title="Logout">
              <Button
                type="text"
                size="small"
                icon={<LogoutOutlined />}
                onClick={this.props.onLogout}
                style={{
                  color: 'var(--danger-color)',
                  border: 'none',
                  width: '44px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  borderRadius: '6px',
                  background: 'rgba(220,38,127,0.1)',
                  minWidth: '44px',
                  touchAction: 'manipulation'
                }}
              />
            </Tooltip>
          </div>
        </div>

        {/* Mobile Corner Controls */}
        <div className="mobile-only mobile-corner-controls">
          {/* Top Left - Current Hand Only (when active) */}
          {(() => {
            const state = store.getState();
            const currentHand = this.getCurrentHand(state);
            const availableHands = this.getAvailableHands(state);
            const canPickHand = this.getCanPickHand(state);
            
            // Only show when there's an active hand or hand selection needed
            if (currentHand || (availableHands.length > 0 && canPickHand)) {
              return (
                <div 
                  className={availableHands.length > 0 ? 'current-hand-indicator' : ''}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    zIndex: 1000,
                    background: currentHand 
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(16, 185, 129, 0.7))'
                      : 'linear-gradient(135deg, rgba(245, 158, 11, 0.9), rgba(249, 115, 22, 0.8))',
                    border: currentHand 
                      ? '2px solid var(--accent-color)' 
                      : '2px solid var(--warning-color)',
                    borderRadius: '12px',
                    padding: '8px 12px',
                    backdropFilter: 'blur(15px)',
                    boxShadow: currentHand
                      ? '0 4px 20px rgba(16, 185, 129, 0.4), 0 0 15px rgba(16, 185, 129, 0.3)'
                      : '0 4px 20px rgba(245, 158, 11, 0.4), 0 0 15px rgba(245, 158, 11, 0.3)',
                    maxWidth: '160px',
                    transform: 'translateZ(0)', // GPU acceleration
                    animation: currentHand ? 'none' : 'mobile-hand-pulse 2s ease-in-out infinite alternate',
                    cursor: availableHands.length > 0 ? 'pointer' : 'default',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={availableHands.length > 0 ? this.showHandModal : undefined}
                >
                  {currentHand ? (
                    <div style={{
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 800,
                      textAlign: 'center',
                      textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                      letterSpacing: '0.5px'
                    }}>
                      {this.props.t(`hands.handNames.${currentHand}`, currentHand)}
                    </div>
                  ) : (
                    <div style={{
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 800,
                      textAlign: 'center',
                      textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                      letterSpacing: '0.5px'
                    }}>
                      {this.props.t('hands.chooseYourHand')}
                    </div>
                  )}
                </div>
              );
            }
            return null; // Don't render anything if no hand info to show
          })()}

          {/* Top Right - Action Buttons (No background, blend into GameBoard) */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            display: 'flex',
            gap: '4px'
          }}>
            <Tooltip title="Scores">
              <Button
                type="text"
                size="small"
                icon={<TrophyOutlined />}
                onClick={this.props.onShowScoreBoard}
                style={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  borderRadius: '50%',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  touchAction: 'manipulation',
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>
{!isMobile() && (
            <Tooltip title="Chat">
              <Button
                type="text"
                size="small"
                icon={<MessageOutlined />}
                onClick={this.props.onShowChat}
                style={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  borderRadius: '50%',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  touchAction: 'manipulation',
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>
            )}
            {(() => {
              // Only show share button for multiplayer games
              const gameState = store.getState() as any;
              const gameMode = gameState.kingGame?.gameState?.gameMode;
              const singlePlayer = gameState.kingGame?.gameState?.singlePlayer;
              
              if (gameMode === 'multiplayer' || (!singlePlayer && gameMode !== 'single')) {
                return (
                  <Tooltip title={this.props.t('share.shareRoom')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<ShareAltOutlined />}
                      onClick={this.handleShareRoom}
                      style={{
                        width: '32px',
                        height: '32px',
                        minWidth: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        borderRadius: '50%',
                        color: 'var(--accent-color)',
                        border: 'none',
                        background: 'rgba(99, 102, 241, 0.15)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.2)',
                        touchAction: 'manipulation',
                        transition: 'all 0.2s ease'
                      }}
                    />
                  </Tooltip>
                );
              }
              return null;
            })()}
            <Tooltip title={this.state.soundEnabled ? "Disable Sound" : "Enable Sound"}>
              <Button
                type="text"
                size="small"
                icon={this.state.soundEnabled ? <SoundFilled /> : <SoundOutlined />}
                onClick={this.toggleSound}
                style={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  borderRadius: '50%',
                  color: this.state.soundEnabled ? 'var(--primary-color)' : 'var(--text-secondary)',
                  border: 'none',
                  background: this.state.soundEnabled ? 'rgba(123,104,238,0.15)' : 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: this.state.soundEnabled ? '0 2px 8px rgba(123,104,238,0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                  touchAction: 'manipulation',
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>
            <Tooltip title="Logout">
              <Button
                type="text"
                size="small"
                icon={<LogoutOutlined />}
                onClick={this.props.onLogout}
                style={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  borderRadius: '50%',
                  color: 'var(--danger-color)',
                  border: 'none',
                  background: 'rgba(239, 68, 68, 0.1)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  touchAction: 'manipulation',
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>
          </div>

          {/* Bottom Left - Turn Indicator (No background, lights only) */}
          <div style={{
            position: 'absolute',
            bottom: '30px',
            left: '30px',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '2px'
          }}>
            {[1, 2, 3, 4].map((index) => (
              <div
                key={index}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: index <= cardsInTheMiddle.length 
                    ? 'var(--primary-color)' 
                    : 'rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  transform: index <= cardsInTheMiddle.length ? 'scale(1.2)' : 'scale(1)',
                  boxShadow: index <= cardsInTheMiddle.length 
                    ? '0 0 8px rgba(99, 102, 241, 0.6)' 
                    : '0 0 4px rgba(255, 255, 255, 0.1)'
                }}
              />
            ))}
          </div>
        </div>

        {/* Top Player */}
        <Row justify="center" className="top-player-row" style={{ marginBottom: 24 }}>
          <Col>
            {this.renderPlayerPosition(top, top === currentPlayer)}
          </Col>
        </Row>

        {/* Top Player Hand */}
        <Row justify="center" className="top-player-hand-row" style={{ marginBottom: 16 }}>
          <Col>
            <div className="other-player-hand">
              {this.renderHand(otherPlayerCards, false, true)}
            </div>
          </Col>
        </Row>

        {/* Middle Row - Left Player, Center Area, Right Player */}
        <Row gutter={[8, 8]} align="middle" className="middle-game-row" style={{ flex: 1 }}>
          {/* Left Player */}
          <Col xs={6} sm={6} md={6} lg={6} xl={6}>
            <div className="side-player-container left-player" style={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              gap: '24px',
              minHeight: '160px',
              justifyContent: 'center',
              position: 'relative',
              padding: '16px 8px',
              width: '100%',
              overflow: 'visible'
            }}>
              <div className="player-avatar left-avatar" style={{ 
                position: 'relative',
                zIndex: 100,
                flexShrink: 0,
                order: 1
              }}>
                {this.renderPlayerPosition(left, left === currentPlayer)}
              </div>
              <div className="player-cards other-player-hand" style={{ 
                position: 'relative',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                order: 2,
                height: '120px', // Fixed height to account for rotation
                width: 'auto'
              }}>
                <div className="hor">
                  {this.renderHand(otherPlayerCards, false, true)}
                </div>
              </div>
            </div>
          </Col>

          {/* Center Play Area */}
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            {this.renderCenterPlayArea()}
          </Col>

          {/* Right Player */}
          <Col xs={6} sm={6} md={6} lg={6} xl={6}>
            <div className="side-player-container right-player" style={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              gap: '24px',
              minHeight: '160px',
              justifyContent: 'center',
              position: 'relative',
              padding: '16px 8px',
              width: '100%',
              overflow: 'visible'
            }}>
              <div className="player-cards other-player-hand" style={{ 
                position: 'relative',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                order: 1,
                height: '120px', // Fixed height to account for rotation
                width: 'auto'
              }}>
                <div className="hor">
                  {this.renderHand(otherPlayerCards, false, true)}
                </div>
              </div>
              <div className="player-avatar right-avatar" style={{ 
                position: 'relative',
                zIndex: 100,
                flexShrink: 0,
                order: 2
              }}>
                {this.renderPlayerPosition(right, right === currentPlayer)}
              </div>
            </div>
          </Col>
        </Row>

        {/* Bottom Player Area - Responsive Layout */}
        <div style={{ marginTop: 24 }}>
          {/* Desktop Layout: Avatar above cards */}
          <div className="desktop-only">
            <Row justify="center" style={{ marginBottom: 16 }}>
              <Col>
                {this.renderPlayerPosition(playerName, playerName === currentPlayer, true)}
              </Col>
            </Row>
            <Row justify="center">
              <Col>
                {this.renderHand(cards, canMove, false)}
              </Col>
            </Row>
          </div>

          {/* Mobile Layout: Cards only (no avatar) */}
          <div className="mobile-only">
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0 16px',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              {/* Cards centered */}
              <div style={{ 
                display: 'flex',
                justifyContent: 'center'
              }}>
                {this.renderHand(cards, canMove, false)}
              </div>
            </div>
          </div>
        </div>

        {/* Game Status */}
        {canMove && (
          <div className="desktop-only" style={{ 
            textAlign: 'center', 
            marginTop: 16, 
            padding: 12, 
            background: 'var(--primary-color)', 
            borderRadius: 'var(--radius-md)'
          }}>
            <Text style={{ color: 'white', fontWeight: 600 }}>
              {this.props.t('game.yourTurnToPlay')}
            </Text>
          </div>
        )}

        {currentPlayer && !canMove && (
          <div className="desktop-only" style={{ 
            textAlign: 'center', 
            marginTop: 16, 
            padding: 12, 
            background: 'var(--bg-tertiary)', 
            borderRadius: 'var(--radius-md)'
          }}>
            <Text style={{ color: 'var(--text-secondary)' }}>
              {this.props.t('game.waitingForPlayer').replace('{playerName}', currentPlayer)}
            </Text>
          </div>
        )}

        {/* Hand Selection Modal */}
        <HandSelectionModal
          visible={showHandModal}
          onClose={this.hideHandModal}
          previewMode={!canPickHand}
        />


      </div>
    );
  }
}

// Functional wrapper component to use hooks
const GameBoard = (props: { onShowScoreBoard?: () => void; onShowChat?: () => void; onLogout?: () => void; }) => {
  const { t } = useTranslation();
  return <GameBoardComponent t={t} {...props} />;
};

export default GameBoard;